import { Graph } from '../graph';
import type {
  OperationData,
  UpdateRel,
  UpdateOrReplaceColumn,
  UpdateExpr,
  UpdateDocument,
  UpdateAgent,
  UpdateTool,
  DeleteOp,
  RenameOp,
  CreateRel,
  CreateExpr,
  CreateDocument,
  CreateAgent,
  CreateColumn,
  CreateTool,
  LockSymbol,
  UnlockSymbol,
  DeleteColOp,
  RenameColOp,
  DuplicateOp,
} from './schema';
import { Result } from 'neverthrow';
import type { BuildExplorer } from './build-explorer';

export type RenameFailure =
  | {
      readonly t: 'symbol-taken';
    }
  | {
      readonly t: 'has-errors';
      readonly errors: { readonly [sym: string]: Graph.GraphError };
    };

export type CommitsBuildFailure =
  | {
      readonly t: 'symbol-taken' | 'symbol-not-mapped';
      readonly sym: string;
    }
  | {
      readonly t: 'ident-locked';
      readonly identity: Graph.Identity;
    }
  | {
      readonly t: 'col-already-exists' | 'col-not-found';
      readonly colName: string;
    }
  | {
      readonly t: 'can-only-add-col-to-rel' | 'can-only-modify-cols-on-rels';
      readonly got: Graph.GraphNode['t'];
    }
  | {
      readonly t: 'cant-update-different-kind';
      readonly curr: Graph.GraphNode['t'];
      readonly attempted: Graph.GraphNode['t'];
    }
  | {
      readonly t: 'circular-reference';
      readonly from: string;
      readonly to: string;
    };

export type CommitsBuildResult = Result<
  BuildExplorer,
  { err: CommitsBuildFailure; at: Graph.Identity }
>;

export const Operations = {
  updateColumn: (
    params: Omit<UpdateOrReplaceColumn, 't' | 'message' | 'k'>
  ): UpdateOrReplaceColumn => ({
    ...params,
    t: 'update-column',
  }),
  replaceColumn: (
    params: Omit<UpdateOrReplaceColumn, 't' | 'message' | 'k'>
  ): UpdateOrReplaceColumn => ({
    ...params,
    t: 'replace-column',
  }),
  updateRel: (
    params: Omit<UpdateRel, 't' | 'message' | 'where' | 'k'> & {
      where?: string | null;
    }
  ): UpdateRel => ({
    ...params,
    t: 'update-rel',
    where: params.where ?? null,
  }),
  updateExpr: (
    params: Omit<UpdateExpr, 't' | 'message' | 'k'> & {
      message?: string | null;
    }
  ): UpdateExpr => ({
    ...params,
    t: 'update-expr',
  }),
  updateDocument: (
    params: Omit<UpdateDocument, 't' | 'message' | 'k'> & {
      message?: string | null;
    }
  ): UpdateDocument => ({
    ...params,
    t: 'update-document',
  }),
  updateAgent: (
    params: Omit<UpdateAgent, 't' | 'message'> & {
      message?: string | null;
    }
  ): UpdateAgent => ({
    ...params,
    t: 'update-agent',
  }),
  updateTool: (
    params: Omit<UpdateTool, 't' | 'message'> & {
      message?: string | null;
    }
  ): UpdateTool => ({
    ...params,
    t: 'update-tool',
  }),
  delete: (
    params: Omit<DeleteOp, 't' | 'message'> & { message?: string | null }
  ): DeleteOp => ({
    ...params,
    t: 'delete',
  }),
  deleteCol: (
    params: Omit<DeleteColOp, 't' | 'message'> & { message?: string | null }
  ): DeleteColOp => ({
    ...params,
    t: 'delete-col',
  }),
  rename: (
    params: Omit<RenameOp, 't' | 'message' | 'rename'> & {
      message?: string | null;
    }
  ): RenameOp => ({
    ...params,
    t: 'rename',
  }),
  renameCol: (params: Omit<RenameColOp, 't'>): RenameColOp => ({
    ...params,
    t: 'rename-col',
  }),
  createRel: (
    params: Omit<CreateRel, 't' | 'message' | 'where' | 'k'> & {
      message?: string | null;
      where?: string | null;
    }
  ): CreateRel => ({
    ...params,
    t: 'create-rel',
    where: params.where ?? null,
  }),
  createExpr: (
    params: Omit<CreateExpr, 't' | 'message'> & {
      message?: string | null;
    }
  ): CreateExpr => ({
    ...params,
    t: 'create-expr',
  }),
  createDocument: (
    params: Omit<CreateDocument, 't' | 'message'> & {
      message?: string | null;
    }
  ): CreateDocument => ({
    ...params,
    t: 'create-document',
  }),
  createAgent: (
    params: Omit<CreateAgent, 't' | 'message'> & {
      message?: string | null;
    }
  ): CreateAgent => ({
    ...params,
    t: 'create-agent',
  }),
  createColumn: (
    params: Omit<CreateColumn, 't' | 'message' | 'k'> & {
      message?: string | null;
    }
  ): CreateColumn => ({
    ...params,
    t: 'create-column',
  }),
  createTool: (
    params: Omit<CreateTool, 't' | 'message'> & {
      message?: string | null;
    }
  ): CreateTool => ({
    ...params,
    t: 'create-tool',
  }),
  lock: (
    params: Omit<LockSymbol, 't' | 'message'> & { message?: string | null }
  ): LockSymbol => ({
    ...params,
    t: 'lock',
  }),
  unlock: (
    params: Omit<UnlockSymbol, 't' | 'message'> & { message?: string | null }
  ): UnlockSymbol => ({
    ...params,
    t: 'unlock',
  }),
  duplicate: (
    params: Omit<DuplicateOp, 't'> & { message?: string | null }
  ): DuplicateOp => ({
    ...params,
    t: 'duplicate',
  }),
};

/**
 * Maps internal node type names to user-friendly display names
 */
const friendlyNodeType = (t: Graph.GraphNode['t']): string => {
  switch (t) {
    case 'rel':
      return 'dataset';
    case 'expr':
      return 'expression';
    case 'document':
      return 'document';
    case 'tool':
      return 'tool';
    case 'agent':
      return 'agent';
    default:
      return t;
  }
};

/**
 * Pretty prints a CommitsBuildFailure error with human-readable messages
 * @param failure - The build failure to format
 * @returns A formatted string describing the error
 */
export const prettyPrintCommitsBuildFailure = (
  failure: CommitsBuildFailure
): string => {
  switch (failure.t) {
    case 'symbol-taken':
      return `The name '${failure.sym}' is already in use`;

    case 'symbol-not-mapped':
      return `'${failure.sym}' could not be found`;

    case 'ident-locked':
      return `This item is locked and cannot be modified`;

    case 'col-already-exists':
      return `Column '${failure.colName}' already exists in the dataset`;

    case 'col-not-found':
      return `Column '${failure.colName}' was not found in the dataset`;

    case 'can-only-add-col-to-rel':
      return `Columns can only be added to datasets, but this is ${
        friendlyNodeType(failure.got) === 'expression' ? 'an' : 'a'
      } ${friendlyNodeType(failure.got)}`;

    case 'can-only-modify-cols-on-rels':
      return `Columns can only be modified on datasets, but this is ${
        friendlyNodeType(failure.got) === 'expression' ? 'an' : 'a'
      } ${friendlyNodeType(failure.got)}`;

    case 'cant-update-different-kind': {
      const curr = friendlyNodeType(failure.curr);
      const attempted = friendlyNodeType(failure.attempted);
      const attemptedArticle =
        attempted === 'expression' || attempted === 'agent' ? 'an' : 'a';
      return `Cannot change a ${curr} into ${attemptedArticle} ${attempted}`;
    }

    default:
      return `Unknown build failure: ${JSON.stringify(failure)}`;
  }
};

export type {
  OperationData,
  UpdateRel,
  UpdateOrReplaceColumn,
  UpdateExpr,
  UpdateDocument,
  UpdateAgent,
  DeleteOp,
  RenameOp,
  CreateRel,
  CreateExpr,
  CreateDocument,
  CreateAgent,
  CreateColumn,
  DuplicateOp,
};
